"use client";
import Link from "next/link";
import Image from "next/image";
import { MoveUpRight } from "lucide-react";
import links from '@/lib/localEndPoints'; 
import { useFetchSWR } from "@/lib/swrConfig";
import apiEndPoints from "@/lib/apiEndPoints"; 
import { ParentCompanyData } from "@/types/companies";
import { SocialMediaData } from '@/types/socialMedia';
import React from "react"; 
import { Default_image } from "@/lib/utils";

const FooterContent = () => {
  const { data: comRes } = useFetchSWR(apiEndPoints.PARRENT_COMPANY);
  const company: ParentCompanyData = comRes?.status === 200 ? comRes.data : {};

  const { data: socialDataRes } = useFetchSWR(
    company?.id ? apiEndPoints.GET_SOCIAL(company.id) : null
  );
  const socialMedias: SocialMediaData[] = socialDataRes?.status === 200 ? socialDataRes.data : [];

  const currentYear = new Date().getFullYear();

  // Optimized logo URL and alt text
  const logoUrl = company?.media?.[0]?.url || '/logo.png'; 
  const logoAlt = company?.name ? `${company.name} logo` : "Company logo";

  return (
    <footer className="bg-primary text-background ">
      {/* Top Logo Section */}
      <div className="border-b">
        <div className="container py-4 flex items-center space-x-3">
          <div className="rounded-full bg-background border p-1">
            <Link href={links.HOME}>
              <Image
                src={logoUrl}
                alt={logoAlt}
                width={40}
                height={40}
                className="object-contain"
                loading="lazy" 
                priority={false} 
              />
            </Link>
          </div>
          <h2 className="text-lg font-semibold">{company?.name || process.env.NEXT_PUBLIC_APP_NAME}</h2>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="container grid grid-cols-1 md:grid-cols-3">
        {/* Left Section */}
        <div className="py-8 md:py-10 space-y-4 md:border-r">
          <div className="text-background space-y-3">
            {company.address && (
              <p className="text-sm">
                <span className="font-bold">Address:</span>
                <br />
                {company.address}
              </p>
            )}
            {company?.emails?.[0] && (
              <p className="text-sm">
                <span className="font-bold">Email:</span>
                <br />
                <Link
                  href={`mailto:${company.emails[0]}`}
                  className="text-background underline"
                >
                  {company.emails[0]}
                </Link>
              </p>
            )}
            {company?.phones?.[0] && (
              <p className="text-sm">
                <span className="font-bold">Contact:</span>
                <br />
                <Link href={`tel:${company.phones[0]}`} className="text-background underline">
                  {company.phones[0]}
                </Link>
              </p>
            )}
          </div>
        </div>

        {/* Middle Section */}
        <div className="md:col-span-2 md:pl-6 mb-8 md:mb-0 md:py-10">
          <h3 className="font-bold text-lg mb-6">Necessary Links</h3>
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div>
              <div className="space-y-3 ">
                {[
                  { href: links.HOME, text: "Home" },
                  { href: links.ABOUT, text: "About us" },
                  { href: links.COMPANIES, text: "Our Companies" },
                  { href: links.SOCIAL, text: "CSR" },
                ].map((item) => (
                  <li key={item.href} className="border-b py-2 px-2 md:px-4 list-none">
                    <Link href={item.href} className="flex items-center justify-between group">
                      <span className="transition-transform duration-300 group-hover:scale-110 text-sm">
                        {item.text}
                      </span>
                      <MoveUpRight size={12} className="transition-transform duration-300 group-hover:-translate-y-2" />
                    </Link>
                  </li>
                ))}
              </div>
            </div>

            <div>
              <div className="space-y-3">
                {[
                  { href: links.NEWS, text: "News & Updates" },
                  { href: links.EVENT, text: "Events" },
                  { href: links.CAREER, text: "Careers" },
                  { href: links.CONTACT, text: "Contact" },
                ].map((item) => (
                  <li key={item.href} className="border-b py-2 px-2 md:px-4 list-none">
                    <Link href={item.href} className="group flex items-center justify-between">
                      <span className="transition-transform duration-300 group-hover:scale-110 text-sm">
                        {item.text}
                      </span>
                      <MoveUpRight size={12} className="transition-transform duration-300 group-hover:-translate-y-2" />
                    </Link>
                  </li>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="border-t py-4">
        <div className="container flex flex-col md:flex-row gap-4 items-center justify-between px-6 text-sm">
          <div className="text-center order-2 md:order-1">
            <p className="">
              &copy; {currentYear} All right reserve by {company?.name || 'Jamana Group'} Developed by -&nbsp;
              <Link href="https://siegecode.com" target="_blank" className="text-background underline">
                Siegecode inc.
              </Link>
            </p>
          </div>

          {/* Social Media */}
          <div className="flex md:mt-0 divide-x text-background border overflow-hidden order-1 md:order-2">
            {socialMedias.map((s) => (
              <Link
                key={s.id}
                href={s.url}
                className="p-2 flex items-center justify-center hover:bg-gray-100 hover:text-foreground transition-all duration-300"
                target="_blank" 
                rel="noopener noreferrer" 
              >
                <div className="relative w-6 h-6 md:w-8 md:h-8">
                  <Image
                    src={s?.media?.[0]?.url || Default_image} 
                    alt={`${s.name} icon`} 
                    fill
                    loading="lazy"
                    priority={false}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};


const Footer = React.memo(FooterContent);
export default Footer;